import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { BranchPicker } from '../components/ui/BranchPicker';
import { EmptyState } from '../components/ui/EmptyState';
import { api } from '../lib/api';
import { useBranches } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type Holiday = { id: string; name: string; holidayDate: string; branchId: string | null };

function HolidayForm({ initial, onSubmit, onCancel, pending }: { initial?: Holiday; onSubmit: (v: { name: string; holidayDate: string; branchId: string | null }) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [holidayDate, setHolidayDate] = useState(initial?.holidayDate ?? '');
  const [branchId, setBranchId] = useState(initial?.branchId ?? '');
  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-3" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name, holidayDate, branchId: branchId || null }); }}>
      <label className="label">Holiday name<input className="field mt-1" required placeholder="e.g. Diwali" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Date<input type="date" className="field mt-1" required value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)} /></label>
      <label className="label">Applies to<BranchPicker clearLabel="Whole company" placeholder="Search branches…" value={branchId} onChange={setBranchId} /></label>
      <div className="flex items-end gap-2 sm:col-span-3">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function HolidaysPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const branches = useBranches();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const holidays = useQuery({ queryKey: ['holidays'], queryFn: async () => (await api.get<Holiday[]>('/api/holidays')).data });
  const branchName = (id: string | null) => (id ? branches.data?.find((b) => b.id === id)?.name ?? 'Branch' : 'Company-wide');

  const create = useMutation({
    mutationFn: (body: { name: string; holidayDate: string; branchId: string | null }) => api.post('/api/holidays', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['holidays'] }); success('Holiday added'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add holiday')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; holidayDate: string; branchId: string | null }) => api.patch(`/api/holidays/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['holidays'] }); success('Holiday updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update holiday')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/holidays/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['holidays'] }); success('Holiday removed'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not remove holiday')),
  });

  const sorted = [...(holidays.data ?? [])].sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Holidays</h1><p className="mt-1 text-muted">Annual holiday calendar.</p></div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button>
      </div>

      {open && <HolidayForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}

      {holidays.isLoading && <Loader />}
      {!holidays.isLoading && sorted.length === 0 && <EmptyState title="No holidays set yet" />}
      <div className="space-y-3">
        {sorted.map((holiday) =>
          editing?.id === holiday.id ? (
            <div className="card" key={holiday.id}><HolidayForm initial={holiday} onSubmit={(v) => update.mutate({ id: holiday.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} /></div>
          ) : (
            <article key={holiday.id} className="card flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <CalendarDays size={18} className="shrink-0 text-muted" />
                <div className="min-w-0"><strong>{holiday.name}</strong><p className="mt-0.5 text-sm text-muted">{new Date(`${holiday.holidayDate}T00:00:00`).toLocaleDateString('en-IN', { dateStyle: 'medium' })} • {branchName(holiday.branchId)}</p></div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(holiday); setOpen(false); }}><Pencil size={16} /></button>
                <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Remove "${holiday.name}"?`)) remove.mutate(holiday.id); }}><Trash2 size={16} /></button>
              </div>
            </article>
          ),
        )}
      </div>
    </>
  );
}
